import { useHistory } from "react-router-dom";
import { EntityDetailProps, EntityDetailTableProps } from "../../common/entity/EntityInterfaces";
import { NotSet } from "../../common/misc/UIconstants";
import { Table } from "../../common/panels/Detail/DetailTable";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { SessionContext } from "../../common/contexts/SessionContext";
import Detail from "../../common/panels/Detail/Detail";
import { DetailsPageTopControlsView } from "../../common/panels/misc/DetailsPageTopControlsView/DetailsPageTopControlsView";
import { getDetailLink, getIndexRoute } from "../../main/Routing";
import { MoreDropdown } from "../../common/buttons/MoreDropdown/MoreDropdown";
import { AlertModal } from "../../common/modals/AlertModal/AlertModal";
import { showtoast } from "../../common/overlays/Toasts/showtoast";
import { DateTimeRenderer } from "../../common/datetime/DateTimeFormatter";
import { LinkEntity } from "../../common/misc/LinkEntity/LinkEntity";
import { CutoffDateInformation } from "./DataSourceForm";
import { BridgeTile } from "../common/tiles/BridgeTile";
import { bridgeConstants } from "../../api/Bridges";
import { DataSourceHistory } from "./common/DataSourceHistory/DataSourceHistory";
import { useQueryClient } from "@tanstack/react-query";
import { DataSourceStatusInfoClient, DataSourceStatusInfoSSH } from "../../api/DataSourceStatus";
import { DatasetsTile } from "../../common/sidebar/tiles/DatasetsTile";
import { Dataset, datasetsConstants } from "../../api/Datasets";
import { DataSourceOverview } from "./common/DataSourceStatusOverview/DataSourceStatusOverview";
import { DataSourceStatus } from "./common/DataSourceStatus/DataSourceStatus";
import { Button } from "../../common/buttons/Button/Button";
import { iconNMRConstants } from "../../api/IconNMR";
import { LucideIcon } from "../../common/icon/LucideIcon";
import { ToggleIndicator } from "../../common/badges/ToggleIndicator/ToggleIndicator";
import { useAutoloadConfigurationsActions } from "../../api/autoload/AutoloadClientsApi";
import { GenericEntityPreviewModal } from "../../Entities/GenericComponents/common/GenericEntityPreviewModal";
import { customValuesListToCustomFieldDict } from "../../Customization/common/CustomFieldConverter";
import { EntitySidebarTileAutosorter } from "../../common/sidebar/tiles/EntitySidebarTile/EntitySidebarTileAutosorter";
import { EntityEditButton } from "../../common/entity/entityComponents/EntityEditButton";
import { EntityCloneButton } from "../../common/entity/entityComponents/EntityCloneButton";
import { DataSourceUpdateModesIconTranslator, DataSourceUpdateModesTranslator } from "../../api/DataSource";
import { dataFormatsConstants } from "../../api/DataFormats";
import { customImportConstants } from "../../api/CustomImports";
import { DatasetDetailTableProps } from "../../Dataset/DatasetsDetailTable";
import { LiveConnectionCallbacks, useListen } from "../../common/signalr/SignalRContext";

export const DataSourceDetailTable = ({
  entity,
  entityConstants,
  fieldLabels,
}: EntityDetailTableProps<"dataSources">) => {
  const history = useHistory();
  const { route } = useContext(SessionContext);
  const [showLastClientStatus, setShowLastClientStatus] = useState<boolean>(
    entity.status?.lastClientStatus?.runState === "Running"
  );
  const [isRunning, setIsRunning] = useState<boolean>(entity.status?.lastClientStatus?.runState === "Running");
  const queryClient = useQueryClient();
  const refetchDataSource = useCallback(
    (updateDatasets?: boolean) => {
      queryClient.invalidateQueries({ queryKey: [entityConstants.resource, "detail", entity.id] });
      queryClient.invalidateQueries({ queryKey: [entityConstants.resource, "list"] });
      queryClient.invalidateQueries({ queryKey: [entityConstants.resource, "ids_only"] });
      queryClient.invalidateQueries({ queryKey: [entityConstants.resource, "suggestions"] });
      if (updateDatasets) {
        queryClient.invalidateQueries({ queryKey: [datasetsConstants.resource, "list"] });
        queryClient.invalidateQueries({ queryKey: [datasetsConstants.resource, "count"] });
      }
    },
    [entity.id, entityConstants.resource, queryClient]
  );

  const DataSourceScheduledCallback: LiveConnectionCallbacks["DataSourceScheduled"] = useCallback(
    (dataSourceId) => {
      if (dataSourceId === entity.id) {
        refetchDataSource();
      }
    },
    [entity.id, refetchDataSource]
  );
  useListen("DataSourceScheduled", DataSourceScheduledCallback);
  const ClientConnectionChangedCallback: LiveConnectionCallbacks["ClientConnectionChanged"] = useCallback(
    (bridgeId) => {
      if (bridgeId === entity.bridge.id) {
        refetchDataSource();
      }
    },
    [entity.bridge.id, refetchDataSource]
  );
  useListen("ClientConnectionChanged", ClientConnectionChangedCallback);
  const DataSourceStatusChangedCallback: LiveConnectionCallbacks["DataSourceStatusChanged"] = useCallback(
    (dataSourceId, data) => {
      if (dataSourceId === entity.id) {
        setIsRunning(data.runState === "Running");
        refetchDataSource();
        setShowLastClientStatus(true);
      }
    },
    [entity.id, refetchDataSource]
  );
  useListen("DataSourceStatusChanged", DataSourceStatusChangedCallback);

  const datasetsUploaded = useMemo(() => {
    switch (entity.bridge.type) {
      case "Client":
        return (entity.status?.lastClientStatus?.info as DataSourceStatusInfoClient | undefined)?.datasetsSubmitted;
      case "SFTP":
        return (entity.status?.lastClientStatus?.info as DataSourceStatusInfoSSH | undefined)?.datasetsUploaded;
      default:
        return;
    }
  }, [entity.bridge.type, entity.status?.lastClientStatus?.info]);

  useEffect(() => {
    if (isRunning && !!datasetsUploaded) {
      queryClient.invalidateQueries({ queryKey: [datasetsConstants.resource, "list"] });
      queryClient.invalidateQueries({ queryKey: [datasetsConstants.resource, "count"] });
    }
  }, [isRunning, queryClient, datasetsUploaded]);

  useEffect(() => {
    setIsRunning(entity.status?.lastClientStatus?.runState === "Running");
    if (entity.status?.lastClientStatus?.runState === "Running") {
      var interval = setInterval(() => {
        refetchDataSource();
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [entity.status?.lastClientStatus?.runState, refetchDataSource]);

  useEffect(() => {
    if (entity.type === "IconNMR")
      history.replace(route(getDetailLink(iconNMRConstants.frontendIndexRoute, entity.id)));
  }, [entity.id, entity.type, history, route]);

  return (
    <>
      <Table noPadding nonFoldable>
        <Table.Head>Details</Table.Head>
        <Table.Body>
          <Table.Body.RowContent
            title={fieldLabels.name}
            content={
              <div className="container_label">
                <div className="container_label_name" style={{ fontWeight: 600 }}>
                  {entity.name ?? NotSet}
                </div>
                <div className="container_label_id">
                  <span style={{ whiteSpace: "nowrap" }}>Data Source-ID: {entity.id}</span>
                </div>
              </div>
            }
          />
          <Table.Body.RowContent
            title={fieldLabels.bridge}
            content={<LinkEntity entityConstants={bridgeConstants} property={entity.bridge} />}
          />
          <Table.Body.RowContent
            title={fieldLabels.type}
            content={
              <div className="flex align-center">
                <label className="label label-soft-secondary" style={{ margin: 0 }}>
                  {entity.type}
                </label>
              </div>
            }
          />

          <Table.Body.RowContent title={fieldLabels.enabled} content={<ToggleIndicator enabled={entity.enabled} />} />
          <Table.Body.RowContent
            title={fieldLabels.intervalInSeconds}
            content={
              <span>{`${entity.intervalInSeconds ? `${Math.round(entity.intervalInSeconds / 60)} min` : NotSet}`}</span>
            }
          />

          <Table.Body.RowContent
            title={fieldLabels.format}
            content={
              <div className="flex row-wrap align-center gap-5" style={{ width: "100%" }}>
                <LinkEntity
                  entityConstants={dataFormatsConstants}
                  property={entity.format}
                  routeOverride={route(getIndexRoute(dataFormatsConstants.frontendIndexRoute))}
                />
              </div>
            }
          />

          {!!entity.fileExcludePatterns?.length && (
            <Table.Body.RowContent
              title={fieldLabels.fileExcludePatterns}
              content={
                <div className="flex row-wrap align-center gap-5" style={{ width: "100%" }}>
                  <LucideIcon name="corner-down-right" color="var(--gray-400)" />
                  {entity.fileExcludePatterns.map((p, index) => (
                    <span
                      key={"file_exclude_" + index}
                      className="label label-soft-default"
                      title={`Regular expression: ${p.regex}`}
                    >
                      {p.name}
                    </span>
                  ))}
                </div>
              }
            />
          )}

          <Table.Body.RowContent
            title={fieldLabels.datasetUpdateMode}
            content={
              <div className="container_label">
                <div className="container_label_name">
                  <span>
                    <LucideIcon name={DataSourceUpdateModesIconTranslator[entity.datasetUpdateMode]} />
                  </span>{" "}
                  {DataSourceUpdateModesTranslator[entity.datasetUpdateMode]}
                </div>
              </div>
            }
          />

          <Table.Body.RowContent
            title={fieldLabels.directories}
            content={
              <div style={{ width: "100%" }}>
                {entity.directories.map((d, index) => (
                  <div
                    key={"directory_" + index}
                    style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    {d}
                  </div>
                ))}
              </div>
            }
          />
          <Table.Body.RowContent
            title={fieldLabels.cutoffDate}
            content={
              <div className="flex align-center gap-5" style={{ width: "100%", justifyContent: "space-between" }}>
                <div className="flex align-center gap-5">
                  {entity.cutoffDate ? (
                    <div>
                      <DateTimeRenderer date={entity.cutoffDate} />
                    </div>
                  ) : (
                    NotSet
                  )}
                </div>
                <CutoffDateInformation />
              </div>
            }
          />
        </Table.Body>
      </Table>
      <Table noPadding>
        <Table.Head>Default dataset values and metadata mapping</Table.Head>
        <Table.Body>
          <Table.Body.RowContent
            title={fieldLabels.datasetTemplate}
            content={
              entity.datasetTemplate ? (
                <GenericEntityPreviewModal<Dataset, Partial<DatasetDetailTableProps>>
                  entityTypeId="datasets"
                  entity={
                    {
                      ...entity.datasetTemplate,
                      creationDate: new Date().toISOString(),
                      customFields: customValuesListToCustomFieldDict(entity.datasetTemplate.customValues),
                    } as Dataset
                  }
                  componentProps={{
                    triggerComponent: ({ setIsOpen }) => (
                      <Button
                        className="btn btn-soft-info btn-xs flex align-center gap-5"
                        onClick={() => setIsOpen(true)}
                      >
                        <LucideIcon name="maximize" />
                        View default values
                      </Button>
                    ),
                    triggerComponentContainerStyle: { padding: 0, margin: 0 },
                    titleOverride: "Default dataset values",
                    hideId: true,
                    detailTableProps: { hideAutomaticallyGeneratedFields: true },
                  }}
                />
              ) : (
                NotSet
              )
            }
          />

          <Table.Body.RowContent
            title={fieldLabels.customImport}
            content={
              entity.customImport?.id ? (
                <LinkEntity entityConstants={customImportConstants} property={entity.customImport} />
              ) : (
                NotSet
              )
            }
          />
        </Table.Body>
      </Table>
      {(entity.enabled || showLastClientStatus) && (
        <Table noPadding>
          <Table.Head>Status</Table.Head>
          <Table.Body>
            <div style={{ margin: "10px 0" }}>
              <DataSourceStatus dataSource={entity} />
            </div>
          </Table.Body>
        </Table>
      )}
      {(entity.enabled || showLastClientStatus) && (
        <Table noPadding>
          <Table.Head>Statistics</Table.Head>
          <Table.Body>
            <div style={{ margin: "10px 0" }}>
              <DataSourceOverview dataSource={entity} />
            </div>
          </Table.Body>
        </Table>
      )}
      <Table noPadding isFolded={entity.enabled} style={{ paddingBottom: "5vh" }}>
        <Table.Head>History</Table.Head>
        <Table.Body>
          <div style={{ padding: "10px 0" }}>
            <DataSourceHistory dataSource={entity} allowLoadMore />
          </div>
        </Table.Body>
      </Table>
    </>
  );
};

export const DataSourcesDetail = ({
  permissions,
  routes,
  entity,
  entityApi,
  entityConstants,
  fieldLabels,
  entityTypeId,
}: EntityDetailProps<"dataSources">) => {
  const history = useHistory();
  const [showModal, setShowModal] = useState<boolean>(false);

  const { editMutationAsync, isLoadingEditMutation } = entityApi;

  const { triggerAutoload, cancelAutoload, clearCache, isRunning, setIsRunning } =
    useAutoloadConfigurationsActions(entity);

  return (
    <Detail
      head={
        <DetailsPageTopControlsView
          entityTypeId={entityTypeId}
          entity={entity}
          entityConstants={entityConstants}
          controls={
            <div className="flex align-center gap-5">
              <Button
                className="btn btn-primary"
                disabled={!permissions.canEdit(entity) || !entity.enabled}
                // loading={isRunning}
                disabledOnLoading={false}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setIsRunning(true);
                  isRunning
                    ? cancelAutoload(entity.id).finally(() => {
                        setIsRunning(false);
                      })
                    : triggerAutoload(entity.id).finally(() => {
                        setIsRunning(false);
                      });
                }}
              >
                <LucideIcon name="power" /> {isRunning ? "Abort" : "Start"}
              </Button>
              <EntityEditButton
                entity={entity}
                entityConstants={entityConstants}
                permissions={permissions}
                routes={routes}
              />

              <MoreDropdown drop="right" btn="btn btn-ghost-secondary">
                <li>
                  {!entity.enabled ? (
                    <Button
                      className="btn btn-sm btn-ghost-success"
                      disabled={!permissions.canEdit(entity)}
                      title={`Enable this ${entityConstants.entityPlural}`}
                      onClick={async () => {
                        await editMutationAsync({ id: entity.id, body: { ...entity, enabled: true } }).catch(() => {});
                      }}
                      loading={isLoadingEditMutation}
                    >
                      <LucideIcon name="toggle-right" /> Enable
                    </Button>
                  ) : (
                    <Button
                      className="btn btn-sm btn-ghost-warning"
                      disabled={!permissions.canEdit(entity)}
                      title={`Disable this ${entityConstants.entityPlural}`}
                      onClick={async () => {
                        await editMutationAsync({ id: entity.id, body: { ...entity, enabled: false } }).catch(() => {});
                      }}
                      loading={isLoadingEditMutation}
                    >
                      <LucideIcon name="toggle-left" /> Disable
                    </Button>
                  )}
                </li>
                <li>
                  <button
                    className="btn btn-sm btn-default"
                    title="Clear cache"
                    onClick={() => clearCache(entity.id)}
                    disabled={!permissions.canEdit(entity)}
                  >
                    <LucideIcon name="refresh-cw" /> Clear cache
                  </button>
                </li>
                <li>
                  <EntityCloneButton
                    entity={entity}
                    entityConstants={entityConstants}
                    routes={routes}
                    permissions={permissions}
                  />
                </li>
                <li className="divider" />
                <li>
                  <button
                    className="btn btn-sm btn-ghost-danger"
                    title="Delete"
                    onClick={() => setShowModal(true)}
                    disabled={!permissions.canDelete(entity)}
                  >
                    <LucideIcon name="trash-2" /> Delete
                  </button>
                </li>
              </MoreDropdown>
              <AlertModal
                type="danger"
                showModal={showModal}
                setShowModal={setShowModal}
                title="Permanently delete data source?"
                description="No datasets will be uploaded via this data source in the future."
                proceedLabel="Delete"
                onProceed={async () => {
                  await entityApi
                    .deleteMutationAsync(
                      {
                        id: entity.id,
                        goBackOnSuccess: false,
                        showToast: false,
                        entityName: "dataSource",
                        params: { deletePermanently: true },
                      },

                      {
                        onSuccess: () => {
                          showtoast("success", `Deleted Data-Source-ID: ${entity.id}.`);
                          history.goBack();
                        },
                      }
                    )
                    .catch((e) => {});
                }}
                loading={entityApi.isLoadingDeleteMutation}
              />
            </div>
          }
        />
      }
      mainStyle={{ padding: "0px 10px" }}
      main={
        <DataSourceDetailTable
          entity={entity}
          entityConstants={entityConstants}
          fieldLabels={fieldLabels}
          entityTypeId={entityTypeId}
        />
      }
      sidebar={
        <EntitySidebarTileAutosorter>
          <BridgeTile bridgeId={entity.bridge.id} />
          <DatasetsTile
            title="Datasets"
            defaultFilters={{
              dataSourceIds: [entity.id],
              // dateAddedFrom: recently,
              sortBy: [
                {
                  property: "ENTERED_ON",
                  sortDirection: "DESC",
                },
              ],
            }}
            excludeFilters={{ dataSourceIds: () => false }}
          />
        </EntitySidebarTileAutosorter>
      }
    />
  );
};
